import { useState, useEffect, useRef } from 'react';

import { useStaticCallback } from '@atlaskit/media-common/useStaticCallback';

import type { MediaClient } from '@confluence/media-client';
import { toError } from '../macroErrorUtils.ts';

type NullableString = string | null | undefined;

interface UseDocumentViewerParams {
	mode: NullableString;
	mediaClient: MediaClient | null | undefined;
	mediaFileId: NullableString;
	collectionName: NullableString;
}

type PageRangeContent = Awaited<ReturnType<MediaClient['mediaStore']['getDocumentContent']>>;

interface UseDocumentViewerResult {
	isViewerLoading: boolean;
	viewerError: Error | null;
	viewerSucceeded: boolean;
	hasTrackedSuccess: boolean;
	thumbnailUrl: string | null;
	getContent: (pageStart: number, pageEnd: number) => Promise<PageRangeContent>;
	getPageImageUrl: (pageNumber: number, zoom: number) => Promise<string>;
	onSuccess: () => void;
}

/**
 * Manages state and callbacks for the DocumentViewer and edit-mode thumbnail.
 *
 * - In display mode: drives the DocumentViewer via getContent/getPageImageUrl callbacks,
 *   tracks loading/success/error state, and cleans up blob URLs on unmount.
 * - In edit mode: fetches a thumbnail URL via mediaClient.getImageUrl.
 */
export function useDocumentViewer({
	mode,
	mediaClient,
	mediaFileId,
	collectionName,
}: UseDocumentViewerParams): UseDocumentViewerResult {
	const [isViewerLoading, setIsViewerLoading] = useState(true);
	const [viewerError, setViewerError] = useState<Error | null>(null);
	const [viewerSucceeded, setViewerSucceeded] = useState(false);
	const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
	const blobUrlsRef = useRef<Set<string>>(new Set());
	const hasTrackedSuccessRef = useRef(false);

	// Cleanup blob URLs on unmount to prevent memory leaks
	useEffect(() => {
		const blobUrls = blobUrlsRef.current;
		return () => {
			blobUrls.forEach((url) => URL.revokeObjectURL(url));
			blobUrls.clear();
		};
	}, []);

	// Fetch thumbnail URL only in editor mode — PDF/export always uses DocumentViewer directly.
	useEffect(() => {
		let isCancelled = false;

		if (mode !== 'editor' || !mediaClient?.getImageUrl || !mediaFileId) {
			setThumbnailUrl(null);
			return;
		}

		Promise.resolve(
			mediaClient.getImageUrl(mediaFileId, {
				collection: collectionName ?? undefined,
			}),
		)
			.then((nextThumbnailUrl) => {
				if (!isCancelled) {
					setThumbnailUrl((nextThumbnailUrl as string) ?? null);
				}
			})
			.catch(() => {
				if (!isCancelled) {
					setThumbnailUrl(null);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [collectionName, mediaClient, mediaFileId, mode]);

	const getContent = useStaticCallback(async (pageStart: number, pageEnd: number) => {
		if (!mediaClient || !mediaFileId) {
			const clientError = new Error('Media client or file ID not available');
			setViewerError(clientError);
			throw clientError;
		}
		try {
			return await mediaClient.mediaStore.getDocumentContent(mediaFileId, {
				pageStart,
				pageEnd,
				collectionName: collectionName ?? undefined,
			});
		} catch (fetchError) {
			const normalizedError = toError(fetchError);
			setViewerError(normalizedError);
			throw normalizedError;
		}
	});

	const getPageImageUrl = useStaticCallback(async (pageNumber: number, zoom: number) => {
		if (!mediaClient || !mediaFileId) {
			const clientError = new Error('Media client or file ID not available');
			setViewerError(clientError);
			throw clientError;
		}
		try {
			const blob = await mediaClient.mediaStore.getDocumentPageImage(mediaFileId, {
				page: pageNumber,
				zoom,
				collectionName: collectionName ?? undefined,
			});
			const newUrl = URL.createObjectURL(blob);
			blobUrlsRef.current.add(newUrl);
			return newUrl;
		} catch (fetchError) {
			const normalizedError = toError(fetchError);
			setViewerError(normalizedError);
			throw normalizedError;
		}
	});

	const onSuccess = useStaticCallback(() => {
		if (!hasTrackedSuccessRef.current) {
			hasTrackedSuccessRef.current = true;
			setViewerSucceeded(true);
		}
		setIsViewerLoading(false);
	});

	return {
		isViewerLoading,
		viewerError,
		viewerSucceeded,
		hasTrackedSuccess: hasTrackedSuccessRef.current,
		thumbnailUrl,
		getContent,
		getPageImageUrl,
		onSuccess,
	};
}
